import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, Calendar, ChevronLeft, ChevronRight, X, RotateCcw, Building2, Users, CheckCircle2, Clock, Briefcase } from 'lucide-react';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { departments, monthNames } from '../leaves/constants';
import { getStatusColor, getPriorityColor, calculateDuration, formatDate } from '../leaves/helpers';
import { confirmAbsence, deleteAbsence, unconfirmAbsence, deleteStagedAbsence } from '../../services/absenceAPIs';
import { Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, IconButton, Popover, Box, Chip } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { IconEyeEdit, IconFileUpload, IconChecks, IconEye, IconUser, IconXboxX } from '@tabler/icons-react';
import { useOffices } from '../../offices';
import { exportAbsenceToExcel } from '../../utils/excelExport';
//Assets
import Store from '../../assets/images/icons/store.png';

// Position options constant
const POSITION_OPTIONS = [
  'Account Manager', 'Bookkeeper', 'Client Adviser', 'Customer Service',
  'Developer', 'Facility', 'HR', 'HR Manager', 'IT Professional',
  'Manager', 'Marketing', 'Personal Assistance', 'Planner', 'Project Manager',
  'Quality Reviewer', 'Software Engineer', 'HQ'
];

const Absence = ({ filters, setFilters, setSelectedAbsence, absences, setSelectToStore,
  setSelectedUpload, unconfirmed, fetchAbsences, IsEmployee, late = false, sick = false, perso = false,
  hideTypeFilter = true, hideExportButtons = false, onFilteredDataChange }) => {
  console.log('table absences:', absences);
  const alertRef = useRef(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openUnconfirmDialog, setOpenUnconfirmDialog] = useState(false);
  const [selectedAbsenceId, setSelectedAbsenceId] = useState(null);
  const [expandedEmployees, setExpandedEmployees] = useState(new Set());
  const [showRecordedBy, setShowRecordedBy] = useState(false);
  const [reason, setReason] = useState('');
  const [stagedId, setStagedId] = useState('');
  const { offices } = useOffices();
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' | 'flat'

  const AbsenceTypeLabels = {
    LATENESS: 'Lateness',
    PARTIAL_DAY: 'Partial Day',
    FULL_DAY: 'Full Day',
    MULTIPLE_DAYS: 'Multiple Days',
    SICKNESS: 'Sickness',
    EARLY_DEPARTURE: 'Early Departure',
    EMERGENCY: 'Emergency',
    PERSONAL: 'Personal',
    APPOINTMENT: 'Appointment',
  };

  const AbsenceSubTypeLabels = {
    SICKNESS: 'Sickness',
    LATENESS: 'Lateness',
    EARLY_DEPARTURE: 'Early Departure',
    EMERGENCY: 'Emergency',
    APPOINTMENT: 'Appointment',
    PERSONAL: 'Personal',
  };

  const getSubTypeColorClass = (ty_pe) => {
    switch (ty_pe) {
      case 'SICKNESS': return 'Leaves-subtype-sick';
      case 'LATENESS': return 'Leaves-subtype-lateness';
      case 'EARLY_DEPARTURE': return 'Leaves-subtype-early';
      case 'EMERGENCY': return 'Leaves-subtype-emergency';
      case 'APPOINTMENT': return 'Leaves-subtype-appointment';
      case 'PERSONAL': return 'Leaves-subtype-personal';
      default: return 'Leaves-subtype-default';
    }
  };


  const deriveAbsenceType = (date, endDate, hours) => {
    if (date && endDate && date !== endDate) {
      return 'MULTIPLE_DAYS';
    }
    const hrs = parseFloat(hours);
    if (hrs >= 8) return 'FULL_DAY';
    if (hrs > 0) return 'PARTIAL_DAY';
    return 'FULL_DAY';
  };

  const getAbsenceData = (absence, unconfirmed) => {
    const date = absence?.date || null;
    const endDate = absence?.endDate || null;
    const hours = absence?.hours || 0;
    const derivedType = deriveAbsenceType(date, endDate, hours);

    if (unconfirmed) {
      return {
        employee: absence?.employee || {},
        type: derivedType,
        ty_pe: absence?.ty_pe || 'Unknown',
        date,
        endDate,
        hours,
        reason: absence?.reason || 'N/A',
        justified: absence?.justified || null,
        recordedBy: absence?.employee?.firstName + " " + absence?.employee?.lastName,
        employeeId: absence?.employee?.employeeId || 'N/A',
        id: absence?.id || absence.stageId || 'unknown',
        stagedId: absence.id || absence.stageId || 'unknown',
        status: absence.status || 'unknown'
      };
    }
    return {
      employee: absence?.employee || {},
      type: derivedType,
      ty_pe: absence.ty_pe || 'Unknown',
      date,
      endDate,
      hours,
      reason: absence.reason || 'N/A',
      justified: absence.justified || null,
      recordedBy: absence.recordedBy || 'Unknown',
      employeeId: absence.employee?.employeeId || 'N/A',
      id: absence.id || 'unknown'
    };
  };

  const getEmployeeDepartment = (emp, abs) => {
    if (!emp && !abs) return '';
    const raw = (emp && typeof emp.department === 'object' ? emp.department?.name : emp?.department)
      || emp?.departmentName
      || abs?.department
      || abs?.departmentName
      || '';
    return typeof raw === 'string' ? raw.trim() : String(raw || '').trim();
  };

  const getEmployeeOffice = (emp, abs) => {
    if (!emp && !abs) return '';
    const raw = (emp && typeof emp.office === 'object' ? emp.office?.name : emp?.office)
      || emp?.officeName
      || abs?.office
      || abs?.officeName
      || '';
    return typeof raw === 'string' ? raw.trim() : String(raw || '').trim();
  };

  const availableOffices = useMemo(() => {
    const set = new Set();
    (offices || []).forEach(office => {
      const name = typeof office === 'object' ? office?.name : office;
      if (name) set.add(name.trim());
    });
    (absences || []).forEach(absence => {
      const data = getAbsenceData(absence, unconfirmed);
      const o = getEmployeeOffice(data.employee, absence);
      if (o) set.add(o);
    });
    return Array.from(set).sort();
  }, [offices, absences, unconfirmed]);

  const availableDepartments = useMemo(() => {
    const set = new Set(departments || []);
    (absences || []).forEach(absence => {
      const data = getAbsenceData(absence, unconfirmed);
      const d = getEmployeeDepartment(data.employee, absence);
      if (d) set.add(d);
    });
    return Array.from(set).sort();
  }, [absences, unconfirmed]);

  const availableYears = useMemo(() => {
    const years = new Set();
    (absences || []).forEach(absence => {
      const data = getAbsenceData(absence, unconfirmed);
      if (data?.date) {
        const year = new Date(data.date).getFullYear();
        if (!isNaN(year)) years.add(year);
      }
    });
    years.add(new Date().getFullYear());
    years.add(new Date().getFullYear() - 1);
    years.add(2025);
    return Array.from(years).sort((a, b) => b - a);
  }, [absences, unconfirmed]);

  useEffect(() => {
    console.log("Absences:", absences);
  }, [absences]);

  const handleConfirmAbsence = async (stageId) => {
    try {
      await confirmAbsence(stageId);
      setSuccessMessage('Absence confirmed successfully');
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 3000);
      if (fetchAbsences) await fetchAbsences();
    } catch (error) {
      setErrorMessage('Failed to confirm absence: ' + (error.response?.data?.message || error.message));
      setSuccessMessage('');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleUnConfirmAbsence = async () => {
    try {
      console.log('id:', stagedId);
      console.log('reason: ', reason);
      await unconfirmAbsence(stagedId, reason);
      setSuccessMessage('Absence rejected successfully');
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 3000);
      if (fetchAbsences) await fetchAbsences();
      handleCloseUnconfirmDialog();
    } catch (error) {
      setErrorMessage('Failed to reject absence: ' + (error.response?.data?.message || error.message));
      setSuccessMessage('');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleOpenDeleteDialog = (absenceId) => {
    setSelectedAbsenceId(absenceId);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setReason('');
    setSelectedAbsenceId(null);
  };

  const handleOpenUnconfirmDialog = (absenceId) => {
    setSelectedAbsenceId(absenceId);
    setOpenUnconfirmDialog(true);
    setReason('');
  };

  const handleCloseUnconfirmDialog = () => {
    setOpenUnconfirmDialog(false);
    setSelectedAbsenceId(null);
  };

  const handleDeleteAbsence = async () => {
    try {
      if (unconfirmed) {
        await deleteStagedAbsence(selectedAbsenceId);
      } else {
        await deleteAbsence(selectedAbsenceId);
      }
      setSuccessMessage('Absence deleted successfully');
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 3000);
      if (fetchAbsences) await fetchAbsences();
      handleCloseDeleteDialog();
    } catch (error) {
      setErrorMessage('Failed to delete absence: ' + (error.response?.data?.message || error.message));
      setSuccessMessage('');
      setTimeout(() => setErrorMessage(''), 5000);
      handleCloseDeleteDialog();
    }
  };


  const formatHoursToDisplay = (hoursDecimal) => {
    if (!hoursDecimal && hoursDecimal !== 0) return '0h';
    const totalHours = parseFloat(hoursDecimal);
    if (isNaN(totalHours)) return 'Invalid';
    if (totalHours < 1) {
      const minutes = Math.round(totalHours * 60);
      return `${minutes} min`;
    }
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}min`;
  };

  const filteredData = absences.filter(absence => {
    const data = getAbsenceData(absence, unconfirmed);

    if (!filters.includeLateness && data.ty_pe === 'LATENESS') return false;

    const absenceDate = data.date && !isNaN(new Date(data.date).getTime()) ? new Date(data.date) : null;
    const absenceEndDate = data.endDate && !isNaN(new Date(data.endDate).getTime()) ? new Date(data.endDate) : absenceDate;
    const absenceMonth = absenceDate ? absenceDate.getMonth() + 1 : null;
    const absenceYear = absenceDate ? absenceDate.getFullYear() : null;

    // === Date range filter (absence date) ===
    let dateInRange = true;
    if (filters.startDate || filters.endDate) {
      const filterStart = filters.startDate ? new Date(filters.startDate) : null;
      const filterEnd = filters.endDate ? new Date(filters.endDate) : null;
      if (filterStart) filterStart.setHours(0, 0, 0, 0);
      if (filterEnd) filterEnd.setHours(23, 59, 59, 999);
      const absenceStart = absenceDate ? new Date(absenceDate) : null;
      const absenceEnd = absenceEndDate ? new Date(absenceEndDate) : absenceStart;
      if (absenceStart && filterStart && absenceEnd && filterEnd) {
        dateInRange = absenceEnd >= filterStart && absenceStart <= filterEnd;
      } else if (absenceStart && filterStart && !filterEnd) {
        dateInRange = absenceEnd >= filterStart;
      } else if (absenceStart && !filterStart && filterEnd) {
        dateInRange = absenceStart <= filterEnd;
      }
    }

    const useDateRange = Boolean(filters.startDate || filters.endDate);
    const useMonthYear = !useDateRange;

    const typeMatch = !filters.type || data.type === filters.type;

    const empDept = getEmployeeDepartment(data.employee, absence);
    const empOffice = getEmployeeOffice(data.employee, absence);

    const deptMatch = !filters.department || (empDept && empDept.toLowerCase() === filters.department.trim().toLowerCase());

    // Position filter match
    const positionMatch = !filters.position ||
      (data.employee?.positions && Array.isArray(data.employee.positions)
        ? data.employee.positions.some(pos =>
          pos.positionName?.toLowerCase().includes(filters.position.toLowerCase())
        )
        : data.employee?.position?.toLowerCase().includes(filters.position.toLowerCase()));

    const officeMatch = !filters.office || (empOffice && empOffice.toLowerCase() === filters.office.trim().toLowerCase());
    const monthMatch = !useMonthYear || !filters.month || (absenceMonth && absenceMonth === parseInt(filters.month));
    const yearMatch = !useMonthYear || !filters.year || (absenceYear && absenceYear === parseInt(filters.year));
    const query = (filters.search || '').toLowerCase().trim();
    const fullName = `${data.employee.firstName || ''} ${data.employee.lastName || ''}`.toLowerCase().trim();
    const searchMatch = !query ||
      (data.employee.firstName?.toLowerCase() || '').includes(query) ||
      (data.employee.lastName?.toLowerCase() || '').includes(query) ||
      fullName.includes(query) ||
      (data.employeeId?.toLowerCase() || '').includes(query);

    const statusMatch = !filters.status || (data.status && data.status.toLowerCase() === filters.status.toLowerCase());

    const finalResult = typeMatch && statusMatch && deptMatch && positionMatch && officeMatch && monthMatch && yearMatch && searchMatch && dateInRange;
    return finalResult;
  });

  const lastFilteredDataRef = useRef([]);
  useEffect(() => {
    if (onFilteredDataChange) {
      const idsCurrent = filteredData.map(a => a.id || a.stageId || '').join(',');
      const idsLast = lastFilteredDataRef.current.map(a => a.id || a.stageId || '').join(',');
      if (idsCurrent !== idsLast) {
        lastFilteredDataRef.current = filteredData;
        onFilteredDataChange(filteredData);
      }
    }
  }, [filteredData, onFilteredDataChange]);

  const groupedAbsences = !IsEmployee && !unconfirmed
    ? filteredData.reduce((acc, absence) => {
      const data = getAbsenceData(absence, unconfirmed);
      const employeeId = data.employee?.id || data.employeeId;
      if (!acc[employeeId]) {
        acc[employeeId] = { employee: data.employee, absences: [] };
      }
      acc[employeeId].absences.push(absence);
      return acc;
    }, {})
    : null;

  const toggleEmployee = (employeeId) => {
    setExpandedEmployees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(employeeId)) newSet.delete(employeeId);
      else newSet.add(employeeId);
      return newSet;
    });
  };

  console.log("Filtered Data:", filteredData.map(a => getAbsenceData(a, unconfirmed)));

  const toLocalDateString = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calendar Filter States & Logic
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [rangeSelectingStart, setRangeSelectingStart] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);

  const getInitialYear = () => {
    if (filters.startDate) {
      const d = new Date(filters.startDate);
      if (!isNaN(d.getTime())) return d.getFullYear();
    }
    if (filters.year) return parseInt(filters.year);
    return new Date().getFullYear();
  };

  const getInitialMonth = () => {
    if (filters.startDate) {
      const d = new Date(filters.startDate);
      if (!isNaN(d.getTime())) return d.getMonth();
    }
    if (filters.month) return parseInt(filters.month) - 1;
    return new Date().getMonth();
  };

  const [viewYear, setViewYear] = useState(getInitialYear);
  const [viewMonth, setViewMonth] = useState(getInitialMonth); // 0 - 11

  const formatDisplayDate = (dateVal) => {
    if (!dateVal) return '';
    if (typeof dateVal === 'string') {
      const parts = dateVal.split('-');
      if (parts.length === 3) {
        return `${parts[2].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[0]}`;
      }
    }
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getDateFilterLabel = () => {
    if (filters.startDate && filters.endDate) {
      if (filters.startDate === filters.endDate) {
        return formatDisplayDate(filters.startDate);
      }
      return `${formatDisplayDate(filters.startDate)} - ${formatDisplayDate(filters.endDate)}`;
    }
    if (filters.startDate) {
      return `From ${formatDisplayDate(filters.startDate)}`;
    }
    if (filters.endDate) {
      return `Until ${formatDisplayDate(filters.endDate)}`;
    }
    if (filters.month && filters.year) {
      return `${monthNames[filters.month - 1]} ${filters.year}`;
    }
    if (filters.month) {
      return monthNames[filters.month - 1];
    }
    if (filters.year) {
      return `Year ${filters.year}`;
    }
    return 'All Dates';
  };

  const hasActiveDateFilter = Boolean(filters.startDate || filters.endDate || filters.month || filters.year);

  const handleOpenCalendar = (e) => {
    setCalendarAnchorEl(e.currentTarget);
    if (filters.startDate) {
      const d = new Date(filters.startDate);
      if (!isNaN(d.getTime())) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      }
    } else if (filters.year) {
      setViewYear(parseInt(filters.year));
      if (filters.month) setViewMonth(parseInt(filters.month) - 1);
    }
    setRangeSelectingStart(null);
    setHoverDate(null);
  };

  const handleCloseCalendar = () => {
    if (rangeSelectingStart) {
      const d = new Date(rangeSelectingStart);
      setFilters(prev => ({
        ...prev,
        startDate: rangeSelectingStart,
        endDate: rangeSelectingStart,
        year: !isNaN(d.getTime()) ? d.getFullYear() : prev.year,
        month: !isNaN(d.getTime()) ? d.getMonth() + 1 : prev.month
      }));
      setRangeSelectingStart(null);
    }
    setHoverDate(null);
    setCalendarAnchorEl(null);
  };

  const handleClearDates = () => {
    setFilters(prev => ({
      ...prev,
      startDate: null,
      endDate: null,
      year: null,
      month: null
    }));
    setRangeSelectingStart(null);
    setHoverDate(null);
    setCalendarAnchorEl(null);
  };

  const isAnyFilterActive = Boolean(
    filters.office ||
    filters.type ||
    filters.status ||
    filters.department ||
    filters.position ||
    filters.search ||
    filters.startDate ||
    filters.endDate ||
    filters.year ||
    filters.month
  );

  const handleResetAllFilters = () => {
    setFilters(prev => ({
      ...prev,
      office: '',
      type: '',
      status: '',
      department: '',
      position: '',
      year: null,
      month: null,
      startDate: null,
      endDate: null,
      search: '',
    }));
    const now = new Date();
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
    setRangeSelectingStart(null);
    setHoverDate(null);
    setCalendarAnchorEl(null);
  };

  const handleSelectDate = (dateStr) => {
    if (!rangeSelectingStart) {
      setRangeSelectingStart(dateStr);
    } else {
      let start = rangeSelectingStart;
      let end = dateStr;
      if (end < start) {
        const tmp = start;
        start = end;
        end = tmp;
      }
      const sDate = new Date(start);
      const eDate = new Date(end);
      const isSameMonth = sDate.getFullYear() === eDate.getFullYear() && sDate.getMonth() === eDate.getMonth();

      setFilters(prev => ({
        ...prev,
        startDate: start,
        endDate: end,
        year: sDate.getFullYear(),
        month: isSameMonth ? sDate.getMonth() + 1 : null
      }));
      setRangeSelectingStart(null);
      setHoverDate(null);
      setCalendarAnchorEl(null);
    }
  };

  const handlePreset = (presetType) => {
    const today = new Date();
    let startStr = null;
    let endStr = null;
    let yearVal = null;
    let monthVal = null;

    if (presetType === 'today') {
      startStr = toLocalDateString(today);
      endStr = startStr;
      yearVal = today.getFullYear();
      monthVal = today.getMonth() + 1;
    } else if (presetType === 'thisMonth') {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      startStr = toLocalDateString(first);
      endStr = toLocalDateString(last);
      yearVal = today.getFullYear();
      monthVal = today.getMonth() + 1;
    } else if (presetType === 'lastMonth') {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      const ref = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      startStr = toLocalDateString(first);
      endStr = toLocalDateString(last);
      yearVal = ref.getFullYear();
      monthVal = ref.getMonth() + 1;
    } else if (presetType === 'thisYear') {
      const first = new Date(today.getFullYear(), 0, 1);
      const last = new Date(today.getFullYear(), 11, 31);
      startStr = toLocalDateString(first);
      endStr = toLocalDateString(last);
      yearVal = today.getFullYear();
      monthVal = null;
    } else if (presetType === 'viewMonth') {
      const first = new Date(viewYear, viewMonth, 1);
      const last = new Date(viewYear, viewMonth + 1, 0);
      startStr = toLocalDateString(first);
      endStr = toLocalDateString(last);
      yearVal = viewYear;
      monthVal = viewMonth + 1;
    }

    setFilters(prev => ({
      ...prev,
      startDate: startStr,
      endDate: endStr,
      year: yearVal,
      month: monthVal
    }));
    setRangeSelectingStart(null);
    setHoverDate(null);
    setCalendarAnchorEl(null);
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(prev => prev - 1);
    } else {
      setViewMonth(prev => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(prev => prev + 1);
    } else {
      setViewMonth(prev => prev + 1);
    }
  };

  const calendarDays = useMemo(() => {
    const days = [];
    const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
    const lastDayOfMonth = new Date(viewYear, viewMonth + 1, 0);
    const numDays = lastDayOfMonth.getDate();

    // Monday=0 ... Sunday=6
    const startDayOfWeek = (firstDayOfMonth.getDay() + 6) % 7;

    const prevMonthLastDay = new Date(viewYear, viewMonth, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const d = new Date(viewYear, viewMonth - 1, prevMonthLastDay - i);
      days.push({
        dateStr: toLocalDateString(d),
        dayNum: prevMonthLastDay - i,
        isCurrentMonth: false,
      });
    }

    for (let d = 1; d <= numDays; d++) {
      const curDate = new Date(viewYear, viewMonth, d);
      days.push({
        dateStr: toLocalDateString(curDate),
        dayNum: d,
        isCurrentMonth: true,
      });
    }

    const remaining = (7 - (days.length % 7)) % 7;
    for (let d = 1; d <= remaining; d++) {
      const nextDate = new Date(viewYear, viewMonth + 1, d);
      days.push({
        dateStr: toLocalDateString(nextDate),
        dayNum: d,
        isCurrentMonth: false,
      });
    }

    return days;
  }, [viewYear, viewMonth]);

  const todayStr = useMemo(() => toLocalDateString(new Date()), []);

  const getDayStatus = (dateStr) => {
    let isStart = false;
    let isEnd = false;
    let isInRange = false;

    if (rangeSelectingStart) {
      if (dateStr === rangeSelectingStart) {
        isStart = true;
      } else if (hoverDate) {
        const minD = rangeSelectingStart < hoverDate ? rangeSelectingStart : hoverDate;
        const maxD = rangeSelectingStart < hoverDate ? hoverDate : rangeSelectingStart;
        if (dateStr === hoverDate) {
          isEnd = true;
        } else if (dateStr > minD && dateStr < maxD) {
          isInRange = true;
        }
      }
    } else if (filters.startDate && filters.endDate) {
      if (dateStr === filters.startDate) isStart = true;
      if (dateStr === filters.endDate) isEnd = true;
      if (dateStr > filters.startDate && dateStr < filters.endDate) isInRange = true;
    } else if (filters.startDate) {
      if (dateStr === filters.startDate) isStart = true;
    }

    return { isStart, isEnd, isInRange };
  };

  // Helper function to format positions for display
  const getPositionsDisplay = (employee) => {
    if (!employee) return 'N/A';

    // Handle array of positions
    if (employee.positions && Array.isArray(employee.positions)) {
      const validPositions = employee.positions.filter(p => p.positionName);
      if (validPositions.length === 0) return 'N/A';
      return validPositions
        .map(p => `${p.positionName} (${p.fte || 1.0})`)
        .join('; ');
    }

    // Handle legacy single position field
    if (employee.position) {
      return `${employee.position} (1.0)`;
    }

    return 'N/A';
  };

  return (
    <>
      <div className="Leaves-requests-container">
        <div className="Leaves-requests-header">
          {/* <h3 className="Leaves-requests-title" style={{ marginBottom: '14px' }}>Absences</h3> */}
          <div ref={alertRef}>
            {errorMessage && <Alert severity="error" sx={{ mb: 2, maxWidth: 800 }}>{errorMessage}</Alert>}
            {successMessage && <Alert severity="success" sx={{ mb: 2, maxWidth: 800 }}>{successMessage}</Alert>}
          </div>
          <div className="Leaves-requests-filters">
            {/* Office Filter */}
            {!IsEmployee && (
              <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                <Building2
                  size={18}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#1976d2',
                    pointerEvents: 'none',
                    zIndex: 1,
                    flexShrink: 0
                  }}
                />
                <select
                  value={filters.office || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, office: e.target.value }))}
                  className="Leaves-filter-select"
                  style={{
                    paddingLeft: '38px',
                    borderColor: filters.office ? '#1976d2' : undefined,
                    fontWeight: filters.office ? 600 : 500,
                  }}
                >
                  <option value="">All Offices</option>
                  {availableOffices.map(officeName => (
                    <option key={officeName} value={officeName}>{officeName}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Period / Type Filter */}
            {!late && (
              <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                <Clock
                  size={18}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#1976d2',
                    pointerEvents: 'none',
                    zIndex: 1,
                    flexShrink: 0
                  }}
                />
                <select
                  value={filters.type}
                  onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                  className="Leaves-filter-select"
                  style={{
                    paddingLeft: '38px',
                    borderColor: filters.type ? '#1976d2' : undefined,
                    fontWeight: filters.type ? 600 : 500,
                  }}
                >
                  <option value="">All Periods</option>
                  <option value="PARTIAL_DAY">Partial Day</option>
                  <option value="FULL_DAY">Full Day</option>
                  <option value="MULTIPLE_DAYS">Multiple Days</option>
                </select>
              </div>
            )}

            {/* Status Filter */}
            {unconfirmed && (
              <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                <CheckCircle2
                  size={18}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#1976d2',
                    pointerEvents: 'none',
                    zIndex: 1,
                    flexShrink: 0
                  }}
                />
                <select
                  value={filters.status || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="Leaves-filter-select"
                  style={{
                    paddingLeft: '38px',
                    borderColor: filters.status ? '#1976d2' : undefined,
                    fontWeight: filters.status ? 600 : 500,
                  }}
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            )}

            {/* Department Filter */}
            {!IsEmployee && (
              <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                <Users
                  size={18}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#1976d2',
                    pointerEvents: 'none',
                    zIndex: 1,
                    flexShrink: 0
                  }}
                />
                <select
                  value={filters.department || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
                  className="Leaves-filter-select"
                  style={{
                    paddingLeft: '38px',
                    borderColor: filters.department ? '#1976d2' : undefined,
                    fontWeight: filters.department ? 600 : 500,
                  }}
                >
                  <option value="">All Departments</option>
                  {availableDepartments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
            )}
            {/* Position Filter
            {!IsEmployee && (
              <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                <Briefcase
                  size={18}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#1976d2',
                    pointerEvents: 'none',
                    zIndex: 1,
                    flexShrink: 0
                  }}
                />
                <select
                  value={filters.position || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, position: e.target.value || null }))}
                  className="Leaves-filter-select"
                  style={{ paddingLeft: '38px' }}
                >
                  <option value="">All Positions</option>
                  {POSITION_OPTIONS.map(position => (
                    <option key={position} value={position}>{position}</option>
                  ))}
                </select>
              </div>
            )} */}
            {/* Merged Date Filter (Year, Month, Start & End Date via Calendar) */}
            <div
              className="Leaves-filter-select"
              onClick={handleOpenCalendar}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                minWidth: '170px',
                justifyContent: 'space-between',
                borderColor: hasActiveDateFilter ? '#1976d2' : undefined,
                fontWeight: hasActiveDateFilter ? 500 : 400,
                userSelect: 'none',
              }}
              title="Click to filter by date range, month, or year"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                <Calendar size={18} style={{ color: '#1976d2', flexShrink: 0 }} />
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {getDateFilterLabel()}
                </span>
              </div>
              {hasActiveDateFilter ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearDates();
                  }}
                  sx={{ p: 0.25, ml: 0.5, color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                  title="Clear date filter"
                >
                  <X size={15} />
                </IconButton>
              ) : (
                <span style={{ fontSize: '10px', opacity: 0.6, marginLeft: '4px' }}>▼</span>
              )}
            </div>

            <Popover
              open={Boolean(calendarAnchorEl)}
              anchorEl={calendarAnchorEl}
              onClose={handleCloseCalendar}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              slotProps={{
                paper: {
                  sx: {
                    p: 2,
                    mt: 1,
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15), 0 8px 10px -6px rgba(0,0,0,0.1)',
                    width: 320,
                    maxWidth: '95vw',
                  }
                }
              }}
            >
              {/* Quick Presets */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Chip
                  label="All Dates"
                  size="small"
                  onClick={handleClearDates}
                  clickable
                  color={!hasActiveDateFilter ? 'primary' : 'default'}
                  variant={!hasActiveDateFilter ? 'filled' : 'outlined'}
                  sx={{ fontSize: '0.75rem', height: '24px' }}
                />
                <Chip
                  label="Today"
                  size="small"
                  onClick={() => handlePreset('today')}
                  clickable
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', height: '24px' }}
                />
                <Chip
                  label="This Month"
                  size="small"
                  onClick={() => handlePreset('thisMonth')}
                  clickable
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', height: '24px' }}
                />
                <Chip
                  label="Last Month"
                  size="small"
                  onClick={() => handlePreset('lastMonth')}
                  clickable
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', height: '24px' }}
                />
                <Chip
                  label="This Year"
                  size="small"
                  onClick={() => handlePreset('thisYear')}
                  clickable
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', height: '24px' }}
                />
              </Box>

              {/* Month / Year Navigator */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, width: '100%', boxSizing: 'border-box' }}>
                <IconButton size="small" onClick={handlePrevMonth} sx={{ p: 0.5, flexShrink: 0 }}>
                  <ChevronLeft size={18} />
                </IconButton>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', flexGrow: 1, px: 0.5 }}>
                  <select
                    value={viewMonth}
                    onChange={(e) => setViewMonth(parseInt(e.target.value))}
                    style={{
                      padding: '4px 6px',
                      fontSize: '0.82rem',
                      height: '30px',
                      borderRadius: '4px',
                      border: '1px solid rgba(0, 0, 0, 0.23)',
                      background: 'inherit',
                      color: 'inherit',
                      minWidth: 'unset',
                      width: '110px',
                      maxWidth: '115px',
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                    }}
                  >
                    {monthNames.map((name, i) => (
                      <option key={name} value={i} style={{ color: '#000', background: '#fff' }}>{name}</option>
                    ))}
                  </select>
                  <select
                    value={viewYear}
                    onChange={(e) => setViewYear(parseInt(e.target.value))}
                    style={{
                      padding: '4px 6px',
                      fontSize: '0.82rem',
                      height: '30px',
                      borderRadius: '4px',
                      border: '1px solid rgba(0, 0, 0, 0.23)',
                      background: 'inherit',
                      color: 'inherit',
                      minWidth: 'unset',
                      width: '75px',
                      maxWidth: '80px',
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                    }}
                  >
                    {availableYears.map(y => (
                      <option key={y} value={y} style={{ color: '#000', background: '#fff' }}>{y}</option>
                    ))}
                  </select>
                </Box>
                <IconButton size="small" onClick={handleNextMonth} sx={{ p: 0.5, flexShrink: 0 }}>
                  <ChevronRight size={18} />
                </IconButton>
              </Box>

              {/* Quick button to select the whole viewed month */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => handlePreset('viewMonth')}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.2 }}
                >
                  Select entire {monthNames[viewMonth]} {viewYear}
                </Button>
              </Box>

              {/* Day of week headers */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: '2px',
                  textAlign: 'center',
                  marginBottom: '4px',
                }}
              >
                {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(dayName => (
                  <div
                    key={dayName}
                    style={{
                      padding: '4px 0',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      color: '#64748b',
                      textTransform: 'uppercase',
                    }}
                  >
                    {dayName}
                  </div>
                ))}
                {calendarDays.map((item, idx) => {
                  const { isStart, isEnd, isInRange } = getDayStatus(item.dateStr);
                  const isToday = item.dateStr === todayStr;
                  const isSelected = isStart || isEnd;

                  let bg = 'transparent';
                  let color = item.isCurrentMonth ? 'inherit' : 'rgba(100, 116, 139, 0.4)';
                  let borderRadius = '6px';

                  if (isSelected) {
                    bg = '#1976d2';
                    color = '#ffffff';
                  } else if (isInRange) {
                    bg = 'rgba(25, 118, 210, 0.12)';
                    borderRadius = '0px';
                  }

                  return (
                    <div
                      key={`${item.dateStr}-${idx}`}
                      onClick={() => handleSelectDate(item.dateStr)}
                      onMouseEnter={() => rangeSelectingStart && setHoverDate(item.dateStr)}
                      style={{
                        padding: '6px 0',
                        cursor: 'pointer',
                        borderRadius,
                        backgroundColor: bg,
                        color,
                        fontSize: '0.85rem',
                        fontWeight: isSelected || isToday ? 600 : 400,
                        border: isToday && !isSelected ? '1px solid #1976d2' : '1px solid transparent',
                        transition: 'background-color 0.12s ease',
                        userSelect: 'none',
                      }}
                    >
                      {item.dayNum}
                    </div>
                  );
                })}
              </div>

              {/* Footer with Reset and Done */}
              <Box
                sx={{
                  mt: 1.5,
                  pt: 1.5,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Button
                  size="small"
                  color="inherit"
                  onClick={handleClearDates}
                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                >
                  Reset
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleCloseCalendar}
                  sx={{ textTransform: 'none', fontSize: '0.8rem', px: 2 }}
                >
                  Done
                </Button>
              </Box>
            </Popover>

            {late && IsEmployee && (
              <Button
                variant={filters.includeLateness ? "contained" : "outlined"}
                color={filters.includeLateness ? "primary" : "inherit"}
                startIcon={<Clock size={16} />}
                onClick={() => setFilters(prev => ({ ...prev, includeLateness: !prev.includeLateness }))}
                sx={{ textTransform: 'none', fontWeight: 'bold', height: '36px' }}
              >
                {filters.includeLateness ? "Hide Lateness" : "Show Lateness"}
              </Button>
            )}
            {!IsEmployee && (
              <div curves="true" className="Leaves-search-container">
                <Search className="Leaves-search-icon" size={18} />
                <input
                  type="text"
                  placeholder="Search employee..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="Leaves-search-input"
                />
              </div>
            )}
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<RotateCcw size={16} />}
              onClick={handleResetAllFilters}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                height: '40px',
                color: isAnyFilterActive ? 'error.main' : 'inherit',
                borderColor: isAnyFilterActive ? 'error.light' : 'rgba(0, 0, 0, 0.23)',
                '&:hover': {
                  borderColor: 'error.main',
                  backgroundColor: 'rgba(211, 47, 47, 0.04)',
                }
              }}
            >
              Reset Filters
            </Button>
            {!IsEmployee && !unconfirmed && (
              <Button
                variant={viewMode === 'flat' ? "contained" : "outlined"}
                color="primary"
                onClick={() => setViewMode(viewMode === 'grouped' ? 'flat' : 'grouped')}
                sx={{ textTransform: 'none', fontWeight: 'bold', height: '40px', ml: 1 }}
              >
                {viewMode === 'grouped' ? "Switch to Flat View" : "Switch to Grouped View"}
              </Button>
            )}
            {/* <Button
              variant={showRecordedBy ? "contained" : "outlined"}
              color={showRecordedBy ? "primary" : "inherit"}
              startIcon={<IconUser size={18} />}
              onClick={() => setShowRecordedBy(!showRecordedBy)}
              sx={{ textTransform: 'none', fontWeight: 'bold', height: '36px', ml: 1 }}
            >
              {showRecordedBy ? "Hide Recorder" : "Show Recorder"}
            </Button> */}
            {!hideExportButtons && (
              <Button
                variant="contained"
                color="success"
                startIcon={<FileDownloadIcon />}
                onClick={() => exportAbsenceToExcel(filteredData, filters, unconfirmed)}
                sx={{ textTransform: 'none', fontWeight: 'bold', ml: 1 }}
              >
                Export to Excel
              </Button>
            )}
          </div>
        </div>
        <div className="Leaves-table-wrapper">
          <table className="Leaves-table">
            <thead className="Leaves-table-head">
              <tr>
                {!IsEmployee && <th className="Leaves-table-header col-priority-5" style={{ width: '40px' }}></th>}
                {!IsEmployee && <th className="Leaves-table-header col-priority-5">Employee</th>}
                {!IsEmployee && <th className="Leaves-table-header col-priority-1">Department</th>}
                {/* {!IsEmployee && <th className="Leaves-table-header">Position</th>} */}
                {!IsEmployee && <th className="Leaves-table-header col-priority-1">Office</th>}
                {/* <th className="Leaves-table-header">Absence Period</th> */}
                {(IsEmployee || unconfirmed) && <th className="Leaves-table-header col-priority-4">Type</th>}
                <th className="Leaves-table-header col-priority-4">Start Date</th>
                <th className="Leaves-table-header col-priority-4">End Date</th>
                <th className="Leaves-table-header col-priority-2">Duration</th>
                {showRecordedBy && <th className="Leaves-table-header col-priority-1">Recorded By</th>}
                {!sick && <th className="Leaves-table-header col-priority-3">Reason</th>}
                {(sick || perso) && <th className="Leaves-table-header col-priority-3">Justified</th>}
                <th className="Leaves-table-header col-priority-5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {IsEmployee ? (
                filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={showRecordedBy ? 10 : 9} className="Leaves-table-cell">No absences found</td>
                  </tr>
                ) : (
                  filteredData.map((a, idx) => {
                    const data = getAbsenceData(a, unconfirmed);
                    return (
                      <tr key={`${data.id}-${idx}`} className="Leaves-table-row">
                        {/* <td className="Leaves-table-cell">
                          <span className={`Leaves-leave-type ${
                            data.type === 'LATENESS' ? 'Leaves-leave-vacation' :
                            data.type === 'MULTIPLE_DAYS' ? 'Leaves-leave-md' :
                            data.type === 'FULL_DAY' ? 'Leaves-leave-fd' :
                            data.type === 'PARTIAL_DAY' ? 'Leaves-leave-pd' : 'Leaves-leave-default'
                          }`}>
                            {AbsenceTypeLabels[data.type] || data.type}
                          </span>
                        </td> */}
                        <td className="Leaves-table-cell col-priority-4">
                          <span className={`Leaves-subtype-badge ${getSubTypeColorClass(data.ty_pe)}`}>
                            {AbsenceSubTypeLabels[data.ty_pe] || data.ty_pe}
                          </span>
                        </td>
                        <td className="Leaves-table-cell col-priority-4">{formatDate(data.date)}</td>
                        <td className="Leaves-table-cell col-priority-4">
                          {data.type === 'MULTIPLE_DAYS' ? formatDate(data.endDate) || '...' : formatDate(data.date) || '...'}
                        </td>
                        <td className="Leaves-table-cell col-priority-2">
                          {data.type === 'MULTIPLE_DAYS' && data.endDate
                            ? `${calculateDuration(data.date, data.endDate)} days`
                            : data.type === 'FULL_DAY'
                              ? '1 day'
                              : data.type === 'PARTIAL_DAY'
                                ? formatHoursToDisplay(data.hours)
                                : 'Open'}
                        </td>
                        {showRecordedBy && <td className="Leaves-table-cell col-priority-1"><span className="Leaves-status-badge">{data.recordedBy}</span></td>}
                        {!sick && <td className="Leaves-table-cell col-priority-3"><span className="Leaves-status-badge">{data.reason}</span></td>}
                        {(sick || perso) && (
                          <td className="Leaves-table-cell col-priority-3" style={{ textAlign: 'center' }}>
                            {(data.ty_pe === 'SICKNESS' || data.ty_pe === 'PERSONAL') && (
                              data.justified ? (
                                <IconChecks size={20} color="green" title="Justified" />
                              ) : (
                                <span style={{ color: 'red' }} title="Not justified">—</span>
                              )
                            )}
                          </td>
                        )}
                        <td className="Leaves-table-cell col-priority-5">
                          <div className="Leaves-actions">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedAbsence(a.absence || a);
                              }}
                              style={{ color: '#1976d2', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                            >
                              <IconEye size={25} />
                            </button>
                            {data.ty_pe == 'SICKNESS' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedUpload(a.absence || a);
                                }}
                                style={{ color: '#1976d2', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                              >
                                <IconFileUpload size={25} />
                              </button>
                            )}
                            {unconfirmed && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleConfirmAbsence(a.stageId);
                                }}
                                style={{ color: '#1976d2', background: 'none', border: 'none', padding: 0, margin: 'auto', cursor: 'pointer' }}
                              >
                                <IconChecks size={25} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )
              ) : unconfirmed ? (
                filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={showRecordedBy ? 12 : 11} className="Leaves-table-cell">No absences found</td>
                  </tr>
                ) : (
                  filteredData.map((a, idx) => {
                    const data = getAbsenceData(a, unconfirmed);
                    return (
                      <tr key={`${data.id}-${idx}`} className="Leaves-table-row">
                        <td className="Leaves-table-cell col-priority-5" style={{ width: '40px' }}></td>
                        <td className="Leaves-table-cell col-priority-5">
                          {unconfirmed && a.status === 'rejected' && (
                            <span style={{ color: 'red', marginRight: '6px', fontSize: '1.75rem' }}>✗</span>
                          )}
                          {data.employee?.firstName} {data.employee?.lastName}
                        </td>
                        <td className="Leaves-table-cell col-priority-1">{(typeof data.employee?.department === 'object' ? data.employee?.department?.name : data.employee?.department) || 'N/A'}</td>
                        {/* <td className="Leaves-table-cell">{getPositionsDisplay(data.employee)}</td> */}
                        <td className="Leaves-table-cell col-priority-1">{(typeof data.employee?.office === 'object' ? data.employee?.office?.name : data.employee?.office) || 'N/A'}</td>
                        {/* <td className="Leaves-table-cell">
                          <span className={`Leaves-leave-type ${
                            data.type === 'LATENESS' ? 'Leaves-leave-vacation' :
                            data.type === 'MULTIPLE_DAYS' ? 'Leaves-leave-md' :
                            data.type === 'FULL_DAY' ? 'Leaves-leave-fd' :
                            data.type === 'PARTIAL_DAY' ? 'Leaves-leave-pd' : 'Leaves-leave-default'
                          }`}>
                            {AbsenceTypeLabels[data.type] || data.type}
                          </span>
                        </td> */}
                        <td className="Leaves-table-cell col-priority-4">
                          <span className={`Leaves-subtype-badge ${getSubTypeColorClass(data.ty_pe)}`}>
                            {AbsenceSubTypeLabels[data.ty_pe] || data.ty_pe}
                          </span>
                        </td>
                        <td className="Leaves-table-cell col-priority-4">{formatDate(data.date)}</td>
                        <td className="Leaves-table-cell col-priority-4">
                          {data.type === 'MULTIPLE_DAYS' ? formatDate(data.endDate) || '...' : formatDate(data.date) || '...'}
                        </td>
                        <td className="Leaves-table-cell col-priority-2">
                          {data.type === 'MULTIPLE_DAYS' && data.endDate
                            ? `${calculateDuration(data.date, data.endDate)} days`
                            : data.type === 'FULL_DAY'
                              ? '1 day'
                              : data.type === 'PARTIAL_DAY'
                                ? formatHoursToDisplay(data.hours)
                                : 'Open'}
                        </td>
                        {showRecordedBy && <td className="Leaves-table-cell col-priority-1"><span className="Leaves-status-badge">{data.recordedBy}</span></td>}
                        {!sick && <td className="Leaves-table-cell col-priority-3"><span className="Leaves-status-badge">{data.reason}</span></td>}
                        {(sick || perso) && (
                          <td className="Leaves-table-cell col-priority-3" style={{ textAlign: 'center' }}>
                            {data.ty_pe === 'SICKNESS' && (
                              data.justified ? (
                                <IconChecks size={20} color="green" title="Justified" />
                              ) : (
                                <span style={{ color: 'red' }} title="Not justified">—</span>
                              )
                            )}
                          </td>
                        )}
                        <td className="Leaves-table-cell col-priority-5">
                          <div className="Leaves-actions">
                            <button onClick={(e) => { e.stopPropagation(); setSelectedAbsence(a.absence || a); }}
                              style={{ color: '#1976d2', background: 'none', border: 'none', padding: 0, margin: 1, cursor: 'pointer' }}>
                              <IconEyeEdit size={25} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setSelectedUpload(a.absence || a); }}
                              style={{ color: '#1976d2', background: 'none', border: 'none', padding: 0, margin: 1, cursor: 'pointer' }}>
                              <IconFileUpload size={25} />
                            </button>
                            {unconfirmed && a.status == 'pending' && (
                              <button onClick={(e) => { e.stopPropagation(); handleConfirmAbsence(a.id); }}
                                className="Leaves-btn Leaves-btn-save">
                                ✓
                              </button>
                            )}
                            {unconfirmed && a.status == 'pending' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setStagedId(a.id);
                                  handleOpenUnconfirmDialog();
                                }}
                                className="Leaves-btn Leaves-btn-delete"
                              >
                                ✗
                              </button>
                            )}

                            {unconfirmed && a.status == 'rejected' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDeleteDialog(data.id);
                                }}
                                style={{ color: '#d32f2f', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                              >
                                <Delete size={25} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )
              ) : viewMode === 'grouped' ? (
                Object.keys(groupedAbsences).length === 0 ? (
                  <tr>
                    <td colSpan={showRecordedBy ? 12 : 11} className="Leaves-table-cell">No absences found</td>
                  </tr>
                ) : (
                  Object.entries(groupedAbsences).map(([employeeId, group]) => {
                    const isExpanded = expandedEmployees.has(employeeId);
                    const employee = group.employee;
                    const totalAbsences = group.absences.length;
                    return (
                      <React.Fragment key={employeeId}>
                        <tr className="Leaves-table-row" style={{ cursor: 'pointer' }} onClick={() => toggleEmployee(employeeId)}>
                          <td className="Leaves-table-cell col-priority-5" style={{ width: '40px' }}></td>
                          <td className="Leaves-table-cell col-priority-5">
                            <div className="Leaves-employee-info">
                              <p className="Leaves-employee-name">
                                {employee?.firstName || 'Unknown'} {employee?.lastName || ''} ({totalAbsences} absence{totalAbsences !== 1 ? 's' : ''})
                              </p>
                            </div>
                          </td>
                          <td className="Leaves-table-cell col-priority-1">{(typeof employee?.department === 'object' ? employee?.department?.name : employee?.department) || 'N/A'}</td>
                          {/* <td className="Leaves-table-cell">{getPositionsDisplay(employee)}</td> */}
                          <td className="Leaves-table-cell col-priority-1">{(typeof employee?.office === 'object' ? employee?.office?.name : employee?.office) || 'N/A'}</td>
                          <td colSpan={showRecordedBy ? 6 : 5} className="Leaves-table-cell col-priority-5">
                            <span style={{ color: '#f5f5f5' }}>Click to {isExpanded ? 'collapse' : 'expand'} details</span>
                          </td>
                        </tr>
                        {isExpanded && group.absences.map((a, idx) => {
                          const data = getAbsenceData(a, unconfirmed);
                          return (
                            <tr key={`${data.id}-${idx}`} className="Leaves-table-row">
                              <td className="Leaves-table-cell col-priority-5"></td>
                              <td className="Leaves-table-cell col-priority-5"></td>
                              <td className="Leaves-table-cell col-priority-1"></td>
                              <td className="Leaves-table-cell col-priority-1"></td>
                              {/* <td className="Leaves-table-cell">
                                <span className={`Leaves-leave-type ${
                                  data.type === 'LATENESS' ? 'Leaves-leave-vacation' :
                                  data.type === 'MULTIPLE_DAYS' ? 'Leaves-leave-md' :
                                  data.type === 'FULL_DAY' ? 'Leaves-leave-fd' :
                                  data.type === 'PARTIAL_DAY' ? 'Leaves-leave-pd' : 'Leaves-leave-default'
                                }`}>
                                  {AbsenceTypeLabels[data.type] || data.type}
                                </span>
                              </td> */}
                              <td className="Leaves-table-cell col-priority-4">{formatDate(data.date)}</td>
                              <td className="Leaves-table-cell col-priority-4">
                                {data.type === 'MULTIPLE_DAYS' ? formatDate(data.endDate) || '...' : formatDate(data.date) || '...'}
                              </td>
                              <td className="Leaves-table-cell col-priority-2">
                                {data.type === 'MULTIPLE_DAYS' && data.endDate
                                  ? `${calculateDuration(data.date, data.endDate)} days`
                                  : data.type === 'FULL_DAY'
                                    ? '1 day'
                                    : data.type === 'PARTIAL_DAY'
                                      ? formatHoursToDisplay(data.hours)
                                      : 'Open'}
                              </td>
                              {showRecordedBy && <td className="Leaves-table-cell col-priority-1"><span className="Leaves-status-badge">{data.recordedBy}</span></td>}
                              {!sick && <td className="Leaves-table-cell col-priority-3"><span className="Leaves-status-badge">{data.reason}</span></td>}
                              {(sick || perso) && (
                                <td className="Leaves-table-cell col-priority-3" style={{ textAlign: 'center' }}>
                                  {(data.ty_pe === 'SICKNESS' || data.ty_pe === 'PERSONAL') && (
                                    data.justified ? (
                                      <IconChecks size={20} color="green" title="Justified" />
                                    ) : (
                                      <span style={{ color: 'red' }} title="Not justified">—</span>
                                    )
                                  )}
                                </td>
                              )}
                              <td className="Leaves-table-cell col-priority-5">
                                <div className="Leaves-actions">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedAbsence(a.absence || a);
                                    }}
                                    style={{ color: '#1976d2', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                  >
                                    <IconEyeEdit size={25} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedUpload(a.absence || a);
                                    }}
                                    style={{ color: '#1976d2', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                  >
                                    <IconFileUpload size={25} />
                                  </button>
                                  {!unconfirmed && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenDeleteDialog(data.id);
                                      }}
                                      style={{ color: '#d32f2f', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                    >
                                      <Delete size={25} />
                                    </button>
                                  )}
                                  {!late && !a.stored && a.ty_pe !== 'SUSPENSION' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectToStore(a.absence || a);
                                      }}
                                      style={{
                                        color: '#1976d2',
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <img
                                        src={Store}
                                        alt="Store"
                                        style={{ width: '30px', height: '35px', borderRadius: '5px' }}
                                      />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })
                )
              ) : (
                filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={showRecordedBy ? 12 : 11} className="Leaves-table-cell">No absences found</td>
                  </tr>
                ) : (
                  filteredData.map((a, idx) => {
                    const data = getAbsenceData(a, unconfirmed);
                    return (
                      <tr key={`${data.id}-${idx}`} className="Leaves-table-row">
                        <td className="Leaves-table-cell col-priority-5" style={{ width: '40px' }}></td>
                        <td className="Leaves-table-cell col-priority-5">
                          {data.employee?.firstName} {data.employee?.lastName}
                        </td>
                        <td className="Leaves-table-cell col-priority-1">{(typeof data.employee?.department === 'object' ? data.employee?.department?.name : data.employee?.department) || 'N/A'}</td>
                        {/* <td className="Leaves-table-cell">{getPositionsDisplay(data.employee)}</td> */}
                        <td className="Leaves-table-cell col-priority-1">{(typeof data.employee?.office === 'object' ? data.employee?.office?.name : data.employee?.office) || 'N/A'}</td>
                        {/* <td className="Leaves-table-cell">
                          <span className={`Leaves-leave-type ${
                            data.type === 'LATENESS' ? 'Leaves-leave-vacation' :
                            data.type === 'MULTIPLE_DAYS' ? 'Leaves-leave-md' :
                            data.type === 'FULL_DAY' ? 'Leaves-leave-fd' :
                            data.type === 'PARTIAL_DAY' ? 'Leaves-leave-pd' : 'Leaves-leave-default'
                          }`}>
                            {AbsenceTypeLabels[data.type] || data.type}
                          </span>
                        </td> */}
                        <td className="Leaves-table-cell col-priority-4">{formatDate(data.date)}</td>
                        <td className="Leaves-table-cell col-priority-4">
                          {data.type === 'MULTIPLE_DAYS' ? formatDate(data.endDate) || '...' : formatDate(data.date) || '...'}
                        </td>
                        <td className="Leaves-table-cell col-priority-2">
                          {data.type === 'MULTIPLE_DAYS' && data.endDate
                            ? `${calculateDuration(data.date, data.endDate)} days`
                            : data.type === 'FULL_DAY'
                              ? '1 day'
                              : data.type === 'PARTIAL_DAY'
                                ? formatHoursToDisplay(data.hours)
                                : 'Open'}
                        </td>
                        {showRecordedBy && <td className="Leaves-table-cell col-priority-1"><span className="Leaves-status-badge">{data.recordedBy}</span></td>}
                        {!sick && <td className="Leaves-table-cell col-priority-3"><span className="Leaves-status-badge">{data.reason}</span></td>}
                        {(sick || perso) && (
                          <td className="Leaves-table-cell col-priority-3" style={{ textAlign: 'center' }}>
                            {(data.ty_pe === 'SICKNESS' || data.ty_pe === 'PERSONAL') && (
                              data.justified ? (
                                <IconChecks size={20} color="green" title="Justified" />
                              ) : (
                                <span style={{ color: 'red' }} title="Not justified">—</span>
                              )
                            )}
                          </td>
                        )}
                        <td className="Leaves-table-cell col-priority-5">
                          <div className="Leaves-actions">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedAbsence(a.absence || a);
                              }}
                              style={{ color: '#1976d2', background: 'none', border: 'none', padding: 0, margin: 1, cursor: 'pointer' }}
                            >
                              <IconEyeEdit size={25} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedUpload(a.absence || a);
                              }}
                              style={{ color: '#1976d2', background: 'none', border: 'none', padding: 0, margin: 1, cursor: 'pointer' }}
                            >
                              <IconFileUpload size={25} />
                            </button>
                            {!unconfirmed && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDeleteDialog(data.id);
                                }}
                                style={{ color: '#d32f2f', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                              >
                                <Delete size={25} />
                              </button>
                            )}
                            {!late && !a.stored && a.ty_pe !== 'SUSPENSION' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectToStore(a.absence || a);
                                }}
                                style={{
                                  color: '#1976d2',
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  cursor: 'pointer',
                                }}
                              >
                                <img
                                  src={Store}
                                  alt="Store"
                                  style={{ width: '30px', height: '35px', borderRadius: '5px' }}
                                />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )
              )}
            </tbody>
          </table>
        </div>
        <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} aria-labelledby="delete-absence-dialog-title">
          <DialogTitle id="delete-absence-dialog-title">Confirm Deletion</DialogTitle>
          <DialogContent>Are you sure you want to delete this absence? This action cannot be undone.</DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} color="primary">Cancel</Button>
            <Button onClick={handleDeleteAbsence} color="error" variant="contained">Delete</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={openUnconfirmDialog} onClose={handleCloseUnconfirmDialog} aria-labelledby="delete-absence-dialog-title">
          <DialogTitle id="delete-absence-dialog-title">Reject Absence</DialogTitle>
          <DialogContent style={{ marginBottom: 2 }}>
            By rejecting this absence record you are notifying the employee that he cannot be dismissed. This action cannot be undone.
            <TextField
              autoFocus
              margin="dense"
              label="Reason for Rejection"
              type="text"
              fullWidth
              variant="outlined"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUnconfirmDialog} color="primary">Cancel</Button>
            <Button onClick={handleUnConfirmAbsence} color="error" variant="contained">Reject</Button>
          </DialogActions>
        </Dialog>
      </div>
      {/* Inline CSS */}
      <style jsx>{`
       :root {
 /* Open Colors - Gray Scale */
 --oc-gray-0: #f8f9fa;
 --oc-gray-1: #f1f3f5;
 --oc-gray-2: #e9ecef;
 --oc-gray-3: #dee2e6;
 --oc-gray-4: #ced4da;
 --oc-gray-5: #adb5bd;
 --oc-gray-6: #868e96;
 --oc-gray-7: #495057;
 --oc-gray-8: #343a40;
 --oc-gray-9: #212529;
 /* Open Colors - Blue */
 --oc-blue-0: #e7f5ff;
 --oc-blue-1: #d0ebff;
 --oc-blue-2: #a5d8ff;
 --oc-blue-3: #74c0fc;
 --oc-blue-4: #4dabf7;
 --oc-blue-5: #339af0;
 --oc-blue-6: #228be6;
 --oc-blue-7: #1c7ed6;
 --oc-blue-8: #1971c2;
 --oc-blue-9: #1864ab;
 /* Open Colors - Violet */
 --oc-violet-0: #f3f0ff;
 --oc-violet-1: #e5dbff;
 --oc-violet-2: #d0bfff;
 --oc-violet-3: #b197fc;
 --oc-violet-4: #9775fa;
 --oc-violet-5: #845ef7;
 --oc-violet-6: #7950f2;
 --oc-violet-7: #7048e8;
 --oc-violet-8: #6741d9;
 --oc-violet-9: #5f3dc4;
 /* Open Colors - Teal */
 --oc-teal-0: #e6fcf5;
 --oc-teal-1: #c3fae8;
 --oc-teal-2: #96f2d7;
 --oc-teal-3: #63e6be;
 --oc-teal-4: #38d9a9;
 --oc-teal-5: #20c997;
 --oc-teal-6: #12b886;
 --oc-teal-7: #0ca678;
 --oc-teal-8: #099268;
 --oc-teal-9: #087f5b;
 /* Open Colors - Red */
 --oc-red-0: #fff5f5;
 --oc-red-1: #ffe3e3;
 --oc-red-2: #ffc9c9;
 --oc-red-3: #ffa8a8;
 --oc-red-4: #ff8787;
 --oc-red-5: #ff6b6b;
 --oc-red-6: #fa5252;
 --oc-red-7: #f03e3e;
 --oc-red-8: #e03131;
 --oc-red-9: #c92a2a;
 /* Open Colors - Orange */
 --oc-orange-0: #fff4e6;
 --oc-orange-1: #ffe8cc;
 --oc-orange-2: #ffd8a8;
 --oc-orange-3: #ffc078;
 --oc-orange-4: #ffa94d;
 --oc-orange-5: #ff922b;
 --oc-orange-6: #fd7e14;
 --oc-orange-7: #f76707;
 --oc-orange-8: #e8590c;
 --oc-orange-9: #d9480f;
 /* Open Colors - Yellow */
 --oc-yellow-0: #fff9db;
 --oc-yellow-1: #fff3bf;
 --oc-yellow-2: #ffec99;
 --oc-yellow-3: #ffe066;
 --oc-yellow-4: #ffd43b;
 --oc-yellow-5: #fcc419;
 --oc-yellow-6: #fab005;
 --oc-yellow-7: #f59f00;
 --oc-yellow-8: #f08c00;
 --oc-yellow-9: #e67700;
 /* Open Colors - Green */
 --oc-green-0: #ebfbee;
 --oc-green-1: #d3f9d8;
 --oc-green-2: #b2f2bb;
 --oc-green-3: #8ce99a;
 --oc-green-4: #69db7c;
 --oc-green-5: #51cf66;
 --oc-green-6: #40c057;
 --oc-green-7: #37b24d;
 --oc-green-8: #2f9e44;
 --oc-green-9: #2b8a3e;
 /* Open Colors - Indigo */
 --oc-indigo-0: #edf2ff;
 --oc-indigo-1: #dbe4ff;
 --oc-indigo-2: #bac8ff;
 --oc-indigo-3: #91a7ff;
 --oc-indigo-4: #748ffc;
 --oc-indigo-5: #5c7cfa;
 --oc-indigo-6: #4c6ef5;
 --oc-indigo-7: #4263eb;
 --oc-indigo-8: #3b5bdb;
 --oc-indigo-9: #364fc7;
}
/* Container Styles */
.Leaves-requests-container {
 background: var(--oc-gray-0);
 border-radius: 16px;
 padding: 32px;
 box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
//  margin: 20px;
}
/* Header Styles */
.Leaves-requests-header {
 margin-bottom: 28px;
}
.Leaves-requests-title {
 font-size: 28px;
 font-weight: 700;
 color: var(--oc-gray-9);
 margin: 0 0 20px 0;
 letter-spacing: -0.5px;
}
/* Filters Section */
.Leaves-requests-filters {
 display: flex;
 gap: 12px;
 flex-wrap: wrap;
 align-items: center;
 background: white;
 padding: 20px;
 border-radius: 12px;
 box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.Leaves-filter-select {
 padding: 10px 16px;
 border: 1.5px solid var(--oc-gray-3);
 border-radius: 8px;
 background: white;
 color: var(--oc-gray-8);
 font-size: 14px;
 font-weight: 500;
 cursor: pointer;
 transition: all 0.2s ease;
 min-width: 140px;
}
.Leaves-filter-select:hover {
 border-color: var(--oc-blue-5);
 box-shadow: 0 0 0 3px var(--oc-blue-0);
}
.Leaves-filter-select:focus {
 outline: none;
 border-color: var(--oc-blue-6);
 box-shadow: 0 0 0 3px var(--oc-blue-1);
}
/* Search Container */
.Leaves-search-container {
 position: relative;
 hight: 40px;
 flex: 1;
 min-width: 200px;
 max-width: 300px;
}
.Leaves-search-icon {
 position: absolute;
 left: 14px;
 top: 50%;
 transform: translateY(-50%);
 color: var(--oc-gray-5);
 width: 18px;
 height: 18px;
 pointer-events: none;
}
.Leaves-search-input {
 width: 100%;
 padding: 10px 16px 10px 44px;
 border: 1.5px solid var(--oc-gray-3);
 border-radius: 8px;
 background: white;
 color: var(--oc-gray-8);
 font-size: 14px;
 font-weight: 500;
 transition: all 0.2s ease;
}
.Leaves-search-input::placeholder {
 color: var(--oc-gray-5);
}
.Leaves-search-input:hover {
 border-color: var(--oc-blue-5);
 box-shadow: 0 0 0 3px var(--oc-blue-0);
}
.Leaves-search-input:focus {
 outline: none;
 border-color: var(--oc-blue-6);
 box-shadow: 0 0 0 3px var(--oc-blue-1);
}
/* Table Wrapper */
.Leaves-table-wrapper {
 container-type: inline-size;
 background: white;
 border-radius: 12px;
 overflow-x: auto;
 box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
/* Table Styles */
.Leaves-table {
 width: 100%;
 border-collapse: separate;
 border-spacing: 0;
}
.Leaves-table-head {
 background: #4A7BA7;
 /* Or use gradient for more depth: */
 /* background: linear-gradient(135deg, #4A7BA7 0%, #3D6A92 100%); */
}
.Leaves-table-header {
 padding: 16px 20px;
 text-align: left;
 font-size: 13px;
 font-weight: 700;
 color: white;
 text-transform: uppercase;
 letter-spacing: 0.5px;
 border-bottom: 2px solid var(--oc-gray-7);
}
.Leaves-table-row {
 transition: all 0.2s ease;
 border-bottom: 1px solid var(--oc-gray-2);
}
.Leaves-table-row:hover {
 background: var(--oc-blue-0);
 transform: translateX(2px);
}
.Leaves-table-row:last-child {
 border-bottom: none;
}
.Leaves-table-cell {
 padding: 16px 20px;
 color: var(--oc-gray-8);
 font-size: 14px;
 font-weight: 500;
 vertical-align: middle;
}
/* Employee Info */
.Leaves-employee-info {
 display: flex;
 flex-direction: column;
 gap: 4px;
}
.Leaves-employee-name {
 margin: 0;
 font-weight: 600;
 color: var(--oc-gray-9);
 font-size: 15px;
}
/* Leave Type Badges */
.Leaves-leave-type {
 display: inline-block;
 padding: 6px 14px;
 border-radius: 20px;
 font-size: 12px;
 font-weight: 700;
 text-transform: uppercase;
 letter-spacing: 0.3px;
 white-space: nowrap;
}
.Leaves-leave-vacation {
 background: linear-gradient(135deg, var(--oc-violet-1) 0%, var(--oc-violet-2) 100%);
 color: var(--oc-violet-9);
 border: 1.5px solid var(--oc-violet-3);
}
.Leaves-leave-md {
 background: linear-gradient(135deg, var(--oc-blue-1) 0%, var(--oc-blue-2) 100%);
 color: var(--oc-blue-9);
 border: 1.5px solid var(--oc-blue-3);
}
.Leaves-leave-fd {
 background: linear-gradient(135deg, var(--oc-teal-1) 0%, var(--oc-teal-2) 100%);
 color: var(--oc-teal-9);
 border: 1.5px solid var(--oc-teal-3);
}
.Leaves-leave-pd {
 background: linear-gradient(135deg, var(--oc-orange-1) 0%, var(--oc-orange-2) 100%);
 color: var(--oc-orange-9);
 border: 1.5px solid var(--oc-orange-3);
}
.Leaves-leave-default {
 background: linear-gradient(135deg, var(--oc-gray-1) 0%, var(--oc-gray-2) 100%);
 color: var(--oc-gray-8);
 border: 1.5px solid var(--oc-gray-3);
}
/* Subtype Badges */
.Leaves-subtype-badge {
 padding: 6px 14px;
 border-radius: 20px;
 font-size: 11px;
 font-weight: 700;
 white-space: nowrap;
 display: inline-block;
 text-transform: uppercase;
 letter-spacing: 0.3px;
}
.Leaves-subtype-sick {
 background: linear-gradient(135deg, var(--oc-red-0) 0%, var(--oc-red-1) 100%);
 color: var(--oc-red-8);
 border: 1.5px solid var(--oc-red-3);
}
.Leaves-subtype-lateness {
 background: linear-gradient(135deg, var(--oc-violet-0) 0%, var(--oc-violet-1) 100%);
 color: var(--oc-violet-8);
 border: 1.5px solid var(--oc-violet-3);
}
.Leaves-subtype-early {
 background: linear-gradient(135deg, var(--oc-yellow-0) 0%, var(--oc-yellow-1) 100%);
 color: var(--oc-yellow-9);
 border: 1.5px solid var(--oc-yellow-3);
}
.Leaves-subtype-emergency {
 background: linear-gradient(135deg, var(--oc-indigo-0) 0%, var(--oc-indigo-1) 100%);
 color: var(--oc-indigo-8);
 border: 1.5px solid var(--oc-indigo-3);
}
.Leaves-subtype-appointment {
 background: linear-gradient(135deg, var(--oc-blue-0) 0%, var(--oc-blue-1) 100%);
 color: var(--oc-blue-8);
 border: 1.5px solid var(--oc-blue-3);
}
.Leaves-subtype-personal {
 background: linear-gradient(135deg, var(--oc-green-0) 0%, var(--oc-green-1) 100%);
 color: var(--oc-green-8);
 border: 1.5px solid var(--oc-green-3);
}
.Leaves-subtype-default {
 background: linear-gradient(135deg, var(--oc-gray-0) 0%, var(--oc-gray-1) 100%);
 color: var(--oc-gray-7);
 border: 1.5px solid var(--oc-gray-3);
}
/* Status Badge */
.Leaves-status-badge {
 display: inline-block;
 padding: 5px 12px;
 border-radius: 16px;
 font-size: 13px;
 font-weight: 600;
 background: var(--oc-gray-1);
 color: var(--oc-gray-7);
 border: 1px solid var(--oc-gray-3);
}
/* Actions */
.Leaves-actions {
 display: flex;
 gap: 8px;
 align-items: center;
 justify-content: flex-start;
}
.Leaves-actions button {
 padding: 8px;
 border-radius: 8px;
 transition: all 0.2s ease;
 display: flex;
 align-items: center;
 justify-content: center;
}
.Leaves-actions button:hover {
 background: var(--oc-gray-1);
 transform: scale(1.1);
}
.Leaves-actions button:active {
 transform: scale(0.95);
}
/* Custom Buttons */
.Leaves-btn {
 padding: 8px 16px;
 border-radius: 8px;
 font-size: 14px;
 font-weight: 600;
 cursor: pointer;
 transition: all 0.2s ease;
 border: none;
 display: inline-flex;
 align-items: center;
 justify-content: center;
 gap: 6px;
}
.Leaves-btn-save {
 background: linear-gradient(135deg, var(--oc-green-5) 0%, var(--oc-green-6) 100%);
 color: white;
 box-shadow: 0 2px 4px rgba(64, 192, 87, 0.2);
}
.Leaves-btn-save:hover {
 background: linear-gradient(135deg, var(--oc-green-6) 0%, var(--oc-green-7) 100%);
 box-shadow: 0 4px 8px rgba(64, 192, 87, 0.3);
 transform: translateY(-1px);
}
.Leaves-btn-delete {
 background: linear-gradient(135deg, var(--oc-red-5) 0%, var(--oc-red-6) 100%);
 color: white;
 box-shadow: 0 2px 4px rgba(250, 82, 82, 0.2);
}
.Leaves-btn-delete:hover {
 background: linear-gradient(135deg, var(--oc-red-6) 0%, var(--oc-red-7) 100%);
 box-shadow: 0 4px 8px rgba(250, 82, 82, 0.3);
 transform: translateY(-1px);
}
/* Alerts */
.MuiAlert-root {
 border-radius: 12px !important;
 font-weight: 500 !important;
 box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
}
.MuiAlert-standardSuccess {
 background: linear-gradient(135deg, var(--oc-green-0) 0%, var(--oc-green-1) 100%) !important;
 color: var(--oc-green-9) !important;
 border: 1.5px solid var(--oc-green-3) !important;
}
.MuiAlert-standardError {
 background: linear-gradient(135deg, var(--oc-red-0) 0%, var(--oc-red-1) 100%) !important;
 color: var(--oc-red-9) !important;
 border: 1.5px solid var(--oc-red-3) !important;
}
/* ========================================================
   Column Priorities (1 = lowest, 5 = highest)
   ======================================================== */

/* Container Queries (Directly adapts to component container width) */
@container (max-width: 1280px) {
  .col-priority-1 {
    display: none !important;
  }
}

@container (max-width: 1050px) {
  .col-priority-2 {
    display: none !important;
  }
}

@container (max-width: 880px) {
  .col-priority-3 {
    display: none !important;
  }
}

@container (max-width: 640px) {
  .col-priority-4 {
    display: none !important;
  }
}

/* Viewport Media Queries */
@media (max-width: 1280px) {
  .col-priority-1 {
    display: none !important;
  }
}

@media (max-width: 1200px) {
 .Leaves-requests-filters {
   flex-direction: column;
   align-items: stretch;
 }
 .Leaves-filter-select,
 .Leaves-search-container {
   width: 100%;
   max-width: none;
 }
 .Leaves-table-header,
 .Leaves-table-cell {
   padding: 10px 12px;
   font-size: 13px;
 }
}

@media (max-width: 1050px) {
  .col-priority-2 {
    display: none !important;
  }
}

@media (max-width: 880px) {
  .col-priority-3 {
    display: none !important;
  }
}

@media (max-width: 640px) {
  .col-priority-4 {
    display: none !important;
  }
}

@media (max-width: 768px) {
 .Leaves-requests-container {
   padding: 16px 12px;
   margin: 10px;
   border-radius: 12px;
 }
 .Leaves-requests-title {
   font-size: 22px;
 }
 .Leaves-table-header,
 .Leaves-table-cell {
   padding: 8px 10px;
   font-size: 12px;
 }
}

@media (max-width: 480px) {
  .Leaves-requests-container {
    padding: 12px 6px;
    margin: 4px;
  }
  .Leaves-table-header,
  .Leaves-table-cell {
    padding: 6px 8px;
    font-size: 11px;
  }
}
/* Animation Enhancements */
@keyframes slideIn {
 from {
   opacity: 0;
   transform: translateY(10px);
 }
 to {
   opacity: 1;
   transform: translateY(0);
 }
}
.Leaves-table-row {
 animation: slideIn 0.3s ease;
}
/* Focus States for Accessibility */
button:focus-visible,
select:focus-visible,
input:focus-visible {
 outline: 3px solid var(--oc-blue-4);
 outline-offset: 2px;
}
/* Scrollbar Styling */
.Leaves-table-wrapper::-webkit-scrollbar {
 height: 8px;
}
.Leaves-table-wrapper::-webkit-scrollbar-track {
 background: var(--oc-gray-1);
 border-radius: 4px;
}
.Leaves-table-wrapper::-webkit-scrollbar-thumb {
 background: var(--oc-gray-4);
 border-radius: 4px;
}
.Leaves-table-wrapper::-webkit-scrollbar-thumb:hover {
 background: var(--oc-gray-5);
}
      `}</style>
    </>
  );
};

export default Absence;